import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type Mod<T> = { default: T };
/** `preload` starts (once) the chunk's download and resolves with its module. */
export type LazyPage<T extends ComponentType<any>> = LazyExoticComponent<T> & { preload: () => Promise<Mod<T>> };

/**
 * React.lazy for a PRERENDERED page, whose first render must not suspend.
 *
 * Why (measured 2026-10-23, phone profile, /tournament/west-end-hamilton: layout shift 1.36): a page that
 * is a plain React.lazy leaves its prerendered markup "dehydrated" until its chunk arrives. Anything that
 * re-renders above it in the meantime (AppShell's setHydrated, a context value) makes React give up on
 * hydrating that boundary: the server's page is thrown away, "Loading…" shows, and the page is drawn
 * again from scratch when the chunk lands (the footer jumps up, then down). A boundary whose lazy child
 * is ALREADY loaded hydrates in the very first pass, before any effect can interrupt it.
 *
 * So: `preload()` is awaited in src/main.tsx (via App's `warmRoute`) before hydrateRoot, and once the
 * module is in hand this lazy hands it to React through a thenable that resolves synchronously (React
 * reads a settled thenable without suspending). On a client-side navigation nothing was preloaded and it
 * behaves exactly like React.lazy.
 */
export const lazyPage = <T extends ComponentType<any>>(load: () => Promise<Mod<T>>): LazyPage<T> => {
  let done: Mod<T> | undefined;
  let pending: Promise<Mod<T>> | undefined;
  const preload = () =>
    (pending ??= load().then(
      (m) => (done = m),
      (e) => {
        pending = undefined; // a failed download may be tried again
        throw e;
      }
    ));
  const Page = lazy<T>(() => (done ? ({ then: (ok: (m: Mod<T>) => void) => ok(done as Mod<T>) } as unknown as Promise<Mod<T>>) : preload()));
  return Object.assign(Page, { preload });
};
